import { useEffect, useId, useRef } from "react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { AlertIcon } from "@/components/ui/icons";

type Tone = "danger" | "warning" | "brand";

interface AlertDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  message?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: Tone;
  /** The glyph in the round badge. Defaults to a warning sign. */
  icon?: (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  /** Disables both buttons while the confirmed action runs. */
  busy?: boolean;
}

const BADGE: Record<Tone, string> = {
  danger: "bg-danger-soft text-danger ring-danger/15",
  warning: "bg-warning-soft text-warning ring-warning/15",
  brand: "bg-brand-soft text-brand ring-brand/15",
};

const CONFIRM_VARIANT = { danger: "danger", warning: "primary", brand: "primary" } as const;

/**
 * A centred "are you sure?" alert: a large round icon, a title, one line of
 * explanation and two buttons.
 *
 * Built on the native `<dialog>`, as `Modal` is, so the browser owns the focus
 * trap and Escape. Focus lands on Cancel, not on the action: Enter on an alert
 * that just appeared should never be the destructive choice.
 */
export function AlertDialog({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "danger",
  icon: Glyph = AlertIcon,
  busy,
}: AlertDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // `showModal()` focuses the first focusable element — Cancel.
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={message ? messageId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(event) => {
        if (event.target === ref.current && !busy) onCancel();
      }}
      className="panel rounded-panel text-content m-auto w-[calc(100%-2rem)] max-w-sm p-0 shadow-lg backdrop:bg-black/50 backdrop:backdrop-blur-[2px]"
    >
      {open ? (
        <div className="animate-pop flex flex-col items-center px-6 pt-7 pb-6 text-center">
          <span
            className={cn("mb-4 grid size-16 place-items-center rounded-full ring-8", BADGE[tone])}
          >
            <Glyph className="size-7" />
          </span>
          <h2 id={titleId} className="text-content text-lg font-bold tracking-tight">
            {title}
          </h2>
          {message ? (
            <p id={messageId} className="text-content-muted mt-1.5 text-sm leading-relaxed">
              {message}
            </p>
          ) : null}
          <div className="mt-6 grid w-full grid-cols-2 gap-2.5">
            <Button variant="secondary" disabled={busy} onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button variant={CONFIRM_VARIANT[tone]} disabled={busy} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </div>
      ) : null}
    </dialog>
  );
}
